import Vue from 'vue';
import { lbApiBeta } from '@/utils/axiosConfig';
import { activityFromSystemMessage } from '@/utils/helpers/matchActivity';

export const MATCHES2_TABS = {
  MATCHES: '1',
  REQUESTS: '2'
};

const VALID_TABS = Object.values(MATCHES2_TABS);
const DEFAULT_TAB = MATCHES2_TABS.MATCHES;

const state = {
  activeTab: DEFAULT_TAB,
  startedMatches: null,
  isLoadingStartedMatches: false,
  startedMatchesError: null,
  startedMatchesHasMore: false,
  inactiveMatches: null,
  isLoadingInactiveMatches: false,
  inactiveMatchesError: null,
  inactiveMatchesHasMore: false,
  newMatches: null,
  isLoadingNewMatches: false,
  newMatchesError: null,
  newMatchesHasMore: false,
  needsResponseFilter: false,
  // Startade's "N olästa" / Favoriter toggle: 'all' | 'unread' |
  // 'favourites'. The backend filters (?unread=true / ?favourites=true;
  // Favoriter also filters Avslutade).
  startedFilter: 'all',
  // A first page (skip 0) is on its way: "Visa fler" waits, so no page is
  // added to a list about to be replaced.
  startedMatchesReloading: false,
  inactiveMatchesReloading: false
};

// Count fetchStartedMatches / fetchInactiveMatches calls, so an answer
// that a later request has overtaken is dropped.
let startedMatchesRequest = 0;
let inactiveMatchesRequest = 0;

// Under Favoriter (Startade and Avslutade) or olästa (Startade) the
// backend filtered the list, so a chain moved here locally only lands in
// it when it matches: a favourite card, an unread chain.
function fitsListFilter(s, listKey, partB, chain) {
  const isFiltered =
    listKey === 'startedMatches' || listKey === 'inactiveMatches';
  if (s.startedFilter === 'favourites' && isFiltered) {
    return !!partB?.isFavourite;
  }
  if (s.startedFilter === 'unread' && listKey === 'startedMatches') {
    return !!chain?.hasUnreadMessage;
  }
  return true;
}

const getters = {
  activeTab: s => s.activeTab,
  startedMatches: s => s.startedMatches,
  isLoadingStartedMatches: s => s.isLoadingStartedMatches,
  startedMatchesError: s => s.startedMatchesError,

  // Top-level matches (Part B) that have at least one started swap
  // alternative — either a direct swap or one or more started triangle
  // swaps. These group all the reachable chats with the same counterpart
  // into a single card.
  startedPartBMatches: s => {
    if (!Array.isArray(s.startedMatches)) return [];
    return s.startedMatches.filter(
      m =>
        m.direct ||
        (Array.isArray(m.startedTriangles) && m.startedTriangles.length > 0) ||
        (Array.isArray(m.unstartedTriangles) && m.unstartedTriangles.length > 0)
    );
  },

  hasStartedMatches: (s, g) => g.startedPartBMatches.length > 0,
  startedMatchesHasMore: s => s.startedMatchesHasMore,
  startedMatchesReloading: s => s.startedMatchesReloading,

  inactiveMatches: s => s.inactiveMatches,
  isLoadingInactiveMatches: s => s.isLoadingInactiveMatches,
  inactiveMatchesError: s => s.inactiveMatchesError,
  inactiveMatchesHasMore: s => s.inactiveMatchesHasMore,
  inactiveMatchesReloading: s => s.inactiveMatchesReloading,

  // Mirror of startedPartBMatches for the inactive tab. Backend is
  // expected to return the same v2 shape ({ hasMore, matches: [...] });
  // if the filter yields nothing despite inactiveMatches being non-empty,
  // relax to return the raw array.
  inactivePartBMatches: s => {
    if (!Array.isArray(s.inactiveMatches)) return [];
    return s.inactiveMatches.filter(
      m =>
        m.direct ||
        (Array.isArray(m.startedTriangles) && m.startedTriangles.length > 0)
    );
  },

  hasInactiveMatches: (s, g) => g.inactivePartBMatches.length > 0,

  newMatches: s => s.newMatches,
  isLoadingNewMatches: s => s.isLoadingNewMatches,
  newMatchesError: s => s.newMatchesError,

  newPartBMatches: s => {
    if (!Array.isArray(s.newMatches)) return [];
    return s.newMatches;
  },

  hasNewMatches: (s, g) => g.newPartBMatches.length > 0,
  newMatchesHasMore: s => s.newMatchesHasMore,

  // Both Part B lists have settled (loaded or failed) at least once — the
  // lists start as null and errors leave them null.
  partBMatchListsSettled: s =>
    (Array.isArray(s.newMatches) || s.newMatchesError !== null) &&
    (Array.isArray(s.startedMatches) || s.startedMatchesError !== null),

  needsResponseFilter: s => s.needsResponseFilter
};

const mutations = {
  SET_ACTIVE_TAB(s, tab) {
    s.activeTab = tab;
  },
  SET_STARTED_MATCHES(s, matches) {
    s.startedMatches = matches;
  },
  SET_LOADING_STARTED_MATCHES(s, isLoading) {
    s.isLoadingStartedMatches = isLoading;
  },
  SET_STARTED_MATCHES_ERROR(s, error) {
    s.startedMatchesError = error;
  },
  SET_STARTED_MATCHES_HAS_MORE(s, hasMore) {
    s.startedMatchesHasMore = hasMore;
  },
  APPEND_STARTED_MATCHES(s, matches) {
    s.startedMatches = [...(s.startedMatches || []), ...matches];
  },
  SET_INACTIVE_MATCHES(s, matches) {
    s.inactiveMatches = matches;
  },
  SET_STARTED_MATCHES_RELOADING(s, value) {
    s.startedMatchesReloading = !!value;
  },
  SET_INACTIVE_MATCHES_RELOADING(s, value) {
    s.inactiveMatchesReloading = !!value;
  },
  SET_LOADING_INACTIVE_MATCHES(s, isLoading) {
    s.isLoadingInactiveMatches = isLoading;
  },
  SET_INACTIVE_MATCHES_ERROR(s, error) {
    s.inactiveMatchesError = error;
  },
  SET_INACTIVE_MATCHES_HAS_MORE(s, hasMore) {
    s.inactiveMatchesHasMore = hasMore;
  },
  APPEND_INACTIVE_MATCHES(s, matches) {
    s.inactiveMatches = [...(s.inactiveMatches || []), ...matches];
  },
  SET_NEW_MATCHES(s, matches) {
    s.newMatches = matches;
  },
  SET_LOADING_NEW_MATCHES(s, isLoading) {
    s.isLoadingNewMatches = isLoading;
  },
  SET_NEW_MATCHES_ERROR(s, error) {
    s.newMatchesError = error;
  },
  SET_NEW_MATCHES_HAS_MORE(s, hasMore) {
    s.newMatchesHasMore = hasMore;
  },
  APPEND_NEW_MATCHES(s, matches) {
    s.newMatches = [...(s.newMatches || []), ...matches];
  },
  SET_NEEDS_RESPONSE_FILTER(s, value) {
    s.needsResponseFilter = !!value;
  },
  SET_STARTED_FILTER(s, value) {
    s.startedFilter = value;
  },
  // The v2 API's isFavourite is per card (the counterpart's listing), so
  // every card for that listing follows.
  SET_FAVOURITE(s, { propositionId, isFavourite }) {
    if (propositionId == null) return;
    const id = String(propositionId);
    [s.newMatches, s.startedMatches, s.inactiveMatches].forEach(list => {
      if (!Array.isArray(list)) return;
      list.forEach(partB => {
        if (partB && String(partB.propositionId) === id) {
          Vue.set(partB, 'isFavourite', !!isFavourite);
        }
      });
    });
  },
  MARK_ALTERNATIVE_UNREAD(s, { alternativeId, lastMessage }) {
    if (alternativeId == null) return;
    const applyTo = node => {
      if (node && node.id === alternativeId) {
        Vue.set(node, 'hasUnreadMessage', true);
        if (lastMessage) {
          Vue.set(node, 'lastMessage', lastMessage);
        }
      }
    };
    const walk = arr => {
      if (!Array.isArray(arr)) return;
      arr.forEach(partB => {
        applyTo(partB.direct);
        (partB.startedTriangles || []).forEach(applyTo);
        (partB.unstartedTriangles || []).forEach(applyTo);
      });
    };
    walk(s.startedMatches);
    walk(s.inactiveMatches);
    walk(s.newMatches);
  },
  MARK_ALTERNATIVE_READ(s, alternativeId) {
    if (alternativeId == null) return;
    const applyTo = node => {
      if (node && node.id === alternativeId) {
        Vue.set(node, 'hasUnreadMessage', false);
      }
    };
    const walk = arr => {
      if (!Array.isArray(arr)) return;
      arr.forEach(partB => {
        applyTo(partB.direct);
        (partB.startedTriangles || []).forEach(applyTo);
        (partB.unstartedTriangles || []).forEach(applyTo);
      });
    };
    walk(s.startedMatches);
    walk(s.inactiveMatches);
    walk(s.newMatches);
  },
  // AB test 'one-match-per-week': a confirmed weekly unlock spends the
  // quota server-side, but the stored nodes still carry the freemium
  // flags from the last fetch. Unlock the target node (so re-clicking
  // it opens the chat, not a paywall) and drop isFreemiumUnlockable
  // from every OTHER node — the quota is used, so a sibling click must
  // lead to the WeeklyMatchLimit paywall, not a second confirm modal
  // that would double-post the unlock.
  MARK_ALTERNATIVE_UNLOCKED(s, alternativeId) {
    if (alternativeId == null) return;
    const applyTo = node => {
      if (!node) return;
      if (node.id === alternativeId) {
        Vue.set(node, 'isFreemiumUnlockable', false);
        Vue.set(node, 'isFreemiumLocked', false);
      } else if (node.isFreemiumUnlockable) {
        Vue.set(node, 'isFreemiumUnlockable', false);
      }
    };
    const walk = arr => {
      if (!Array.isArray(arr)) return;
      arr.forEach(partB => {
        applyTo(partB.direct);
        (partB.startedTriangles || []).forEach(applyTo);
        (partB.unstartedTriangles || []).forEach(applyTo);
      });
    };
    walk(s.startedMatches);
    walk(s.inactiveMatches);
    walk(s.newMatches);
  },
  UPSERT_CHAIN(s, chain) {
    if (!chain || chain.id == null) return;

    // If the chain currently lives inside a partB in newMatches, move the
    // entire partB across to startedMatches (merging with any existing
    // partB there). All alternatives for the same counterpart belong
    // grouped under the started card once any one of them transitions.
    // Guarded on chain.isActive !== false so an inactivation falls through
    // to the strip-and-place logic below and lands in inactiveMatches
    // instead of being yanked into the active section.
    if (chain.isActive !== false && Array.isArray(s.newMatches)) {
      const matchesChain = c => c && c.id === chain.id;
      const newIdx = s.newMatches.findIndex(
        pb =>
          pb &&
          ((pb.direct && matchesChain(pb.direct)) ||
            (Array.isArray(pb.startedTriangles) &&
              pb.startedTriangles.some(matchesChain)) ||
            (Array.isArray(pb.unstartedTriangles) &&
              pb.unstartedTriangles.some(matchesChain)))
      );

      if (newIdx >= 0) {
        const sourcePartB = s.newMatches[newIdx];
        const nextNew = s.newMatches.slice();
        nextNew.splice(newIdx, 1);
        s.newMatches = nextNew;

        const startedList = Array.isArray(s.startedMatches)
          ? s.startedMatches.slice()
          : [];
        const existingIdx = startedList.findIndex(
          p => p && p.propositionId === sourcePartB.propositionId
        );
        const existing = existingIdx >= 0 ? startedList[existingIdx] : null;

        // Merge the two partBs' alternatives, dropping any old copies of
        // the upserted chain so we can re-place it in the correct slot
        // based on its updated status.
        const dedupe = arr => {
          const seen = new Set();
          return arr.filter(c => {
            if (!c || c.id === chain.id) return false;
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
        };
        const merged = {
          ...(existing || sourcePartB),
          direct: null,
          startedTriangles: dedupe([
            ...((existing && existing.startedTriangles) || []),
            ...(sourcePartB.startedTriangles || [])
          ]),
          unstartedTriangles: dedupe([
            ...((existing && existing.unstartedTriangles) || []),
            ...(sourcePartB.unstartedTriangles || [])
          ])
        };
        const carriedDirect =
          (existing && existing.direct) || sourcePartB.direct || null;
        if (carriedDirect && carriedDirect.id !== chain.id) {
          merged.direct = carriedDirect;
        }

        const isDirect =
          typeof chain.id === 'string' && chain.id.split('-').length === 2;
        if (isDirect) {
          merged.direct = chain;
        } else if (chain.hasMessages) {
          merged.startedTriangles = [chain, ...merged.startedTriangles];
        } else {
          merged.unstartedTriangles = [chain, ...merged.unstartedTriangles];
        }

        if (!fitsListFilter(s, 'startedMatches', merged, chain)) return;
        if (existingIdx >= 0) startedList.splice(existingIdx, 1);
        startedList.unshift(merged);
        s.startedMatches = startedList;
        return;
      }
    }

    // Mirror of the newMatches→startedMatches block above for the
    // inactive→active transition. When a chain that currently lives
    // inside a partB in inactiveMatches becomes active again, move the
    // whole partB to startedMatches (merging with any existing partB
    // there). Without this, only the upserted chain relocates and the
    // remaining inactive sibling alternatives stay behind, surfacing
    // the same counterpart in two sections at once.
    if (chain.isActive !== false && Array.isArray(s.inactiveMatches)) {
      const matchesChain = c => c && c.id === chain.id;
      console.log('matchesChain:', matchesChain);
      const inactiveIdx = s.inactiveMatches.findIndex(
        pb =>
          pb &&
          ((pb.direct && matchesChain(pb.direct)) ||
            (Array.isArray(pb.startedTriangles) &&
              pb.startedTriangles.some(matchesChain)) ||
            (Array.isArray(pb.unstartedTriangles) &&
              pb.unstartedTriangles.some(matchesChain)))
      );
      console.log('inactiveIdx:', inactiveIdx);

      if (inactiveIdx >= 0) {
        const sourcePartB = s.inactiveMatches[inactiveIdx];
        const nextInactive = s.inactiveMatches.slice();
        nextInactive.splice(inactiveIdx, 1);
        s.inactiveMatches = nextInactive;

        const startedList = Array.isArray(s.startedMatches)
          ? s.startedMatches.slice()
          : [];
        const existingIdx = startedList.findIndex(
          p => p && p.propositionId === sourcePartB.propositionId
        );
        const existing = existingIdx >= 0 ? startedList[existingIdx] : null;

        const dedupe = arr => {
          const seen = new Set();
          return arr.filter(c => {
            if (!c || c.id === chain.id) return false;
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
        };
        const merged = {
          ...(existing || sourcePartB),
          direct: null,
          startedTriangles: dedupe([
            ...((existing && existing.startedTriangles) || []),
            ...(sourcePartB.startedTriangles || [])
          ]),
          unstartedTriangles: dedupe([
            ...((existing && existing.unstartedTriangles) || []),
            ...(sourcePartB.unstartedTriangles || [])
          ])
        };
        const carriedDirect =
          (existing && existing.direct) || sourcePartB.direct || null;
        if (carriedDirect && carriedDirect.id !== chain.id) {
          merged.direct = carriedDirect;
        }

        const isDirect =
          typeof chain.id === 'string' && chain.id.split('-').length === 2;
        if (isDirect) {
          merged.direct = chain;
        } else if (chain.hasMessages) {
          merged.startedTriangles = [chain, ...merged.startedTriangles];
        } else {
          merged.unstartedTriangles = [chain, ...merged.unstartedTriangles];
        }

        if (!fitsListFilter(s, 'startedMatches', merged, chain)) return;
        if (existingIdx >= 0) startedList.splice(existingIdx, 1);
        startedList.unshift(merged);
        s.startedMatches = startedList;
        return;
      }
    }

    // Strip the chain from every partB that currently holds it. Capture
    // the first partB it lived in so we can reuse its propositionId/
    // residences when the target list doesn't already have a partB to
    // attach to.
    let referencePartB = null;

    const stripFromList = list => {
      if (!Array.isArray(list)) return list;
      const out = [];
      for (let i = 0; i < list.length; i++) {
        const partB = list[i];
        if (!partB) {
          out.push(partB);
          continue;
        }
        let pb = partB;
        let removed = false;

        if (pb.direct && pb.direct.id === chain.id) {
          if (!referencePartB) referencePartB = pb;
          pb = { ...pb, direct: null };
          removed = true;
        }

        const stripFrom = key => {
          if (!Array.isArray(pb[key])) return;
          const idx = pb[key].findIndex(c => c && c.id === chain.id);
          if (idx < 0) return;
          if (!referencePartB) referencePartB = pb;
          const arr = pb[key].slice();
          arr.splice(idx, 1);
          pb = { ...pb, [key]: arr };
          removed = true;
        };
        stripFrom('startedTriangles');
        stripFrom('unstartedTriangles');

        if (!removed) {
          out.push(partB);
          continue;
        }

        const hasAnyChains =
          pb.direct ||
          (Array.isArray(pb.startedTriangles) &&
            pb.startedTriangles.length > 0) ||
          (Array.isArray(pb.unstartedTriangles) &&
            pb.unstartedTriangles.length > 0);
        if (hasAnyChains) out.push(pb);
      }
      return out;
    };

    s.startedMatches = stripFromList(s.startedMatches);
    s.newMatches = stripFromList(s.newMatches);
    s.inactiveMatches = stripFromList(s.inactiveMatches);

    if (!referencePartB) {
      // Chain isn't in any currently-loaded list. If the inactive
      // section hasn't been opened yet it could have lived there, so
      // build a synthetic partB from chain.id (format
      // "<myPropId>-<partBPropId>[-<altPropId>]") and let placement
      // continue — the next inactive/list fetch will overwrite this
      // stub with the canonical partB metadata.
      if (Array.isArray(s.inactiveMatches)) return;
      const partBPropositionId =
        typeof chain.id === 'string' && chain.id.includes('-')
          ? chain.id.split('-')[1]
          : null;
      if (partBPropositionId == null) return;
      referencePartB = {
        propositionId: partBPropositionId,
        residences: chain.residences || [],
        direct: null,
        startedTriangles: [],
        unstartedTriangles: []
      };
    }

    let targetListKey;
    if (chain.isActive === false) {
      // Skip placement when the inactive list hasn't been loaded yet —
      // expanding the section will fetch fresh data, so inserting into
      // a null list would falsely flip it to "loaded but partial".
      if (!Array.isArray(s.inactiveMatches)) return;
      targetListKey = 'inactiveMatches';
    } else if (chain.firstInteractionAt == null) {
      targetListKey = 'newMatches';
    } else {
      targetListKey = 'startedMatches';
    }

    const isDirect =
      typeof chain.id === 'string' && chain.id.split('-').length === 2;
    let targetSlot;
    if (isDirect) {
      targetSlot = 'direct';
    } else if (chain.hasMessages) {
      targetSlot = 'startedTriangles';
    } else {
      targetSlot = 'unstartedTriangles';
    }

    const targetList = Array.isArray(s[targetListKey])
      ? s[targetListKey].slice()
      : [];
    const targetPartBIdx = targetList.findIndex(
      p => p && p.propositionId === referencePartB.propositionId
    );

    const targetPartB =
      targetPartBIdx >= 0
        ? { ...targetList[targetPartBIdx] }
        : {
            ...referencePartB,
            direct: null,
            startedTriangles: [],
            unstartedTriangles: []
          };

    // It has left the list it was in; a filtered list only takes it when
    // it matches.
    if (!fitsListFilter(s, targetListKey, targetPartB, chain)) return;

    if (targetSlot === 'direct') {
      targetPartB.direct = chain;
    } else {
      const arr = Array.isArray(targetPartB[targetSlot])
        ? targetPartB[targetSlot].slice()
        : [];
      const idx = arr.findIndex(c => c && c.id === chain.id);
      if (idx >= 0) arr.splice(idx, 1);
      arr.unshift(chain);
      targetPartB[targetSlot] = arr;
    }

    // When a chain is promoted into startedMatches, sibling chains in
    // the same partB that still live in newMatches should follow — the
    // backend treats the whole partB as "started" once any chain has
    // interactions, and without this fold the user would see half of
    // the partB strand in the "new" section until a full refetch.
    if (targetListKey === 'startedMatches' && Array.isArray(s.newMatches)) {
      const orphanIdx = s.newMatches.findIndex(
        p => p && p.propositionId === referencePartB.propositionId
      );
      if (orphanIdx >= 0) {
        const orphan = s.newMatches[orphanIdx];
        const seenIds = new Set();
        const seed = c => {
          if (c && c.id) seenIds.add(c.id);
        };
        seed(targetPartB.direct);
        (targetPartB.startedTriangles || []).forEach(seed);
        (targetPartB.unstartedTriangles || []).forEach(seed);
        const fold = arr =>
          (arr || []).filter(c => {
            if (!c || !c.id || seenIds.has(c.id)) return false;
            seenIds.add(c.id);
            return true;
          });
        if (!targetPartB.direct && orphan.direct) {
          targetPartB.direct = orphan.direct;
        }
        targetPartB.startedTriangles = [
          ...(targetPartB.startedTriangles || []),
          ...fold(orphan.startedTriangles)
        ];
        targetPartB.unstartedTriangles = [
          ...(targetPartB.unstartedTriangles || []),
          ...fold(orphan.unstartedTriangles)
        ];
        s.newMatches = s.newMatches.filter((_, i) => i !== orphanIdx);
      }
    }

    if (targetPartBIdx >= 0) {
      targetList.splice(targetPartBIdx, 1);
    }
    targetList.unshift(targetPartB);

    s[targetListKey] = targetList;
  },
  UPDATE_ALTERNATIVE_LAST_MESSAGE(s, { alternativeId, lastMessage }) {
    const applyTo = node => {
      if (node && node.id === alternativeId) {
        Vue.set(node, 'lastMessage', lastMessage);
      }
    };
    const walk = arr => {
      if (!Array.isArray(arr)) return;
      arr.forEach(partB => {
        applyTo(partB.direct);
        (partB.startedTriangles || []).forEach(applyTo);
        (partB.unstartedTriangles || []).forEach(applyTo);
      });
    };
    walk(s.startedMatches);
    walk(s.inactiveMatches);
    walk(s.newMatches);
  },
  // Updates lastMessage on the alternative matching `data.matchId` across
  // all three lists (started/inactive/new). Mirrors the v1
  // matches/updateLastMessage but keyed on alternative id rather than
  // chatRoomId — v2 partB nodes don't carry chatRoomId. Incoming
  // (non-sender, non-system) messages also flip hasUnreadMessage so the
  // card badges update without waiting for a refetch — except when the
  // match is the currently open chat, which the chat panel will mark
  // read on its own.
  UPDATE_LAST_MESSAGE(s, { data, selectedMatchId = null } = {}) {
    if (!data || data.matchId == null) return;

    const lastMessage = {
      message: data.content,
      senderName: data.senderId === -1 ? 'SYSTEM' : null,
      sentAt: data.createdAt
    };

    // A status row that ends the match marks it ended too, so the row
    // turns red at once instead of after a reload. One that reopens it
    // only says one party is back (another may still have it ended):
    // Matches2 refetches the chain and the server decides.
    const ends =
      data.senderId === -1 && activityFromSystemMessage(data.content) === false;

    const applyTo = node => {
      if (!node || node.id !== data.matchId) return;
      Vue.set(node, 'lastMessage', lastMessage);
      if (ends) {
        Vue.set(node, 'isActive', false);
      }
      if (!data.isSender && node.id !== selectedMatchId) {
        Vue.set(node, 'hasUnreadMessage', true);
      }
    };
    const walk = arr => {
      if (!Array.isArray(arr)) return;
      arr.forEach(partB => {
        applyTo(partB.direct);
        (partB.startedTriangles || []).forEach(applyTo);
        (partB.unstartedTriangles || []).forEach(applyTo);
      });
    };
    walk(s.startedMatches);
    walk(s.inactiveMatches);
    walk(s.newMatches);
  }
};

const actions = {
  // A heart clicked anywhere in the app: the favourites store hands the
  // change here, as it does to propositions and swapList.
  favouriteSocketUpdate(context, { targetPropositionId, favourite }) {
    context.commit('SET_FAVOURITE', {
      propositionId: targetPropositionId,
      isFavourite: favourite
    });
  },

  setActiveTab(context, { tab, router }) {
    const nextTab = VALID_TABS.includes(tab) ? tab : DEFAULT_TAB;

    if (context.state.activeTab !== nextTab) {
      context.commit('SET_ACTIVE_TAB', nextTab);
    }

    if (router) {
      const currentQuery = router.currentRoute.query;
      if (currentQuery.tab === nextTab) return;

      const nextQuery = { ...currentQuery, tab: nextTab };
      router.replace({ query: nextQuery }).catch(err => {
        // Swallow NavigationDuplicated — harmless and already-at-target.
        if (err && err.name !== 'NavigationDuplicated') throw err;
      });
    }
  },

  loadTabFromQuery(context, route) {
    const queryTab = route && route.query ? route.query.tab : null;
    const nextTab = VALID_TABS.includes(queryTab) ? queryTab : DEFAULT_TAB;

    if (context.state.activeTab !== nextTab) {
      context.commit('SET_ACTIVE_TAB', nextTab);
    }
  },

  // silent: reload without the section spinner (switching the Startade
  // filter keeps the current list on screen until the new one arrives).
  async fetchStartedMatches(
    context,
    { propositionId, skip = 0, matchId = null, silent = false } = {}
  ) {
    if (!propositionId) return;
    // A page added now would land on a list about to be replaced.
    if (skip > 0 && context.state.startedMatchesReloading) return;

    // Only toggle the full-section spinner on the initial load; pagination
    // should keep the existing list visible beneath the Load-more button.
    const showSpinner = skip === 0 && !silent;
    if (showSpinner) {
      context.commit('SET_LOADING_STARTED_MATCHES', true);
    }
    if (skip === 0) context.commit('SET_STARTED_MATCHES_RELOADING', true);
    context.commit('SET_STARTED_MATCHES_ERROR', null);
    const request = ++startedMatchesRequest;

    try {
      const params = new URLSearchParams();
      if (skip > 0) params.set('skip', String(skip));
      else if (matchId) params.set('matchId', String(matchId));
      if (context.state.needsResponseFilter) {
        params.set('needsResponse', 'true');
      }
      if (context.state.startedFilter === 'unread') {
        params.set('unread', 'true');
      } else if (context.state.startedFilter === 'favourites') {
        params.set('favourites', 'true');
      }
      const qs = params.toString();
      const url = `/api/matches/v2/${propositionId}${qs ? `?${qs}` : ''}`;
      const { data } = await lbApiBeta.get(url);

      // A later request (e.g. another filter) has overtaken this one.
      if (request !== startedMatchesRequest) return;

      // v2 shape: { hasMore: bool, matches: [{ propositionId, residences,
      // direct, startedTriangles, unstartedTriangles }] }
      const matches = Array.isArray(data?.matches) ? data.matches : [];

      if (skip > 0) {
        context.commit('APPEND_STARTED_MATCHES', matches);
      } else {
        context.commit('SET_STARTED_MATCHES', matches);
      }
      context.commit('SET_STARTED_MATCHES_HAS_MORE', !!data?.hasMore);
    } catch (e) {
      if (request !== startedMatchesRequest) return;
      context.commit('SET_STARTED_MATCHES_ERROR', e);
      if (skip === 0) {
        context.commit('SET_STARTED_MATCHES', []);
      }
    } finally {
      // The latest load ends the spinner and the reload, also those of a
      // load it overtook.
      if (request === startedMatchesRequest) {
        if (showSpinner || context.state.isLoadingStartedMatches) {
          context.commit('SET_LOADING_STARTED_MATCHES', false);
        }
        if (skip === 0) context.commit('SET_STARTED_MATCHES_RELOADING', false);
      }
    }
  },

  // The Startade filter Favoriter filters Avslutade too (olästa does
  // not). silent: as for fetchStartedMatches.
  async fetchInactiveMatches(
    context,
    { propositionId, skip = 0, matchId = null, silent = false } = {}
  ) {
    if (!propositionId) return;
    // A page added now would land on a list about to be replaced.
    if (skip > 0 && context.state.inactiveMatchesReloading) return;

    const showSpinner = skip === 0 && !silent;
    if (showSpinner) {
      context.commit('SET_LOADING_INACTIVE_MATCHES', true);
    }
    if (skip === 0) context.commit('SET_INACTIVE_MATCHES_RELOADING', true);
    context.commit('SET_INACTIVE_MATCHES_ERROR', null);
    const request = ++inactiveMatchesRequest;

    try {
      const params = new URLSearchParams();
      if (skip > 0) params.set('skip', String(skip));
      else if (matchId) params.set('matchId', String(matchId));
      if (context.state.needsResponseFilter) {
        params.set('needsResponse', 'true');
      }
      if (context.state.startedFilter === 'favourites') {
        params.set('favourites', 'true');
      }
      const qs = params.toString();
      const url = `/api/matches/v2/${propositionId}/inactive${
        qs ? `?${qs}` : ''
      }`;
      const { data } = await lbApiBeta.get(url);

      // A later request (e.g. another filter) has overtaken this one.
      if (request !== inactiveMatchesRequest) return;

      // Assumed to mirror the v2 /matches shape; see getter note.
      const matches = Array.isArray(data?.matches) ? data.matches : [];

      if (skip > 0) {
        context.commit('APPEND_INACTIVE_MATCHES', matches);
      } else {
        context.commit('SET_INACTIVE_MATCHES', matches);
      }
      context.commit('SET_INACTIVE_MATCHES_HAS_MORE', !!data?.hasMore);
    } catch (e) {
      if (request !== inactiveMatchesRequest) return;
      context.commit('SET_INACTIVE_MATCHES_ERROR', e);
      if (skip === 0) {
        context.commit('SET_INACTIVE_MATCHES', []);
      }
    } finally {
      // The latest load ends the spinner and the reload, also those of a
      // load it overtook.
      if (request === inactiveMatchesRequest) {
        if (showSpinner || context.state.isLoadingInactiveMatches) {
          context.commit('SET_LOADING_INACTIVE_MATCHES', false);
        }
        if (skip === 0) {
          context.commit('SET_INACTIVE_MATCHES_RELOADING', false);
        }
      }
    }
  },

  // silent: as for fetchStartedMatches (the track keeps its scroll).
  async fetchNewMatches(
    context,
    { propositionId, currentSize = 0, matchId = null, silent = false } = {}
  ) {
    if (!propositionId) return;

    // Only show full loading state on initial load — pagination should
    // not hide the existing track (which would destroy the scroll ref).
    const showSpinner = currentSize === 0 && !silent;
    if (showSpinner) {
      context.commit('SET_LOADING_NEW_MATCHES', true);
    }
    context.commit('SET_NEW_MATCHES_ERROR', null);

    try {
      const params = new URLSearchParams();
      params.set('currentSize', String(currentSize));
      if (matchId && currentSize === 0) {
        params.set('matchId', String(matchId));
      }
      if (context.state.needsResponseFilter) {
        params.set('needsResponse', 'true');
      }
      const url = `/api/matches/v2/${propositionId}/new?${params.toString()}`;

      const { data } = await lbApiBeta.get(url);

      const matches = Array.isArray(data?.matches) ? data.matches : [];

      if (currentSize > 0) {
        context.commit('APPEND_NEW_MATCHES', matches);
      } else {
        context.commit('SET_NEW_MATCHES', matches);
      }
      context.commit('SET_NEW_MATCHES_HAS_MORE', !!data?.hasMore);
    } catch (e) {
      context.commit('SET_NEW_MATCHES_ERROR', e);
      if (currentSize === 0) {
        context.commit('SET_NEW_MATCHES', []);
      }
    } finally {
      if (showSpinner) {
        context.commit('SET_LOADING_NEW_MATCHES', false);
      }
    }
  },

  // AB test 'one-match-per-week': consume the weekly quota by unlocking
  // one chat. Must succeed before the chat opens — the backend keeps the
  // room locked otherwise.
  async unlockMatch(context, { propositionId, matchId }) {
    try {
      await lbApiBeta.post(`/api/matches/unlock/${propositionId}/${matchId}`);
      return true;
    } catch (error) {
      window.Logger?.log?.(error);
      return false;
    }
  },

  async refreshMatchStatusCounts(context, { propositionId }) {
    if (!propositionId) return;
    try {
      const { data: counts } = await lbApiBeta.get(
        `/api/matches/v2/${propositionId}/summary`
      );
      if (counts) {
        context.commit('matches/setMatchStatusCounts', counts, {
          root: true
        });
      }
    } catch (_) {
      // Best-effort; counts will resync on next navigation.
    }
  },

  async refetchSingleMatchV2(context, { myPropositionId, matchId }) {
    if (!myPropositionId || !matchId) return null;
    try {
      const { data } = await lbApiBeta.get(
        `/api/matches/v2/${myPropositionId}/${matchId}`
      );
      const chain = data || null;
      if (chain) {
        context.commit('UPSERT_CHAIN', chain);

        // Pull fresh section counts from the summary endpoint so the
        // matches-section-count badges reflect the post-upsert state.
        try {
          const { data: counts } = await lbApiBeta.get(
            `/api/matches/v2/${myPropositionId}/summary`
          );
          if (counts) {
            context.commit('matches/setMatchStatusCounts', counts, {
              root: true
            });
          }
        } catch (_) {
          // Summary refresh is best-effort; counts will resync on next
          // navigation if this fetch fails.
        }
      }
      return chain;
    } catch (e) {
      window.Logger?.log('FaTaL eRroR: ', e);
      return null;
    }
  },

  // Open a chat for a v2 alternative. v2 nodes don't carry a chatRoom
  // directly, so we use the existing find-or-mock endpoint to get one.
  // For a direct (2-party) swap the alternative's propositionId equals
  // Part B's, so we send only [me, partB]; for a triangle we send the
  // full [me, partB, partC]. This mirrors the legacy mockChatRoom
  // helper in src/store/modules/matches/services.js. Returns the chat
  // room object on success, or null on failure.
  async openAlternative(
    _context,
    { partB, alternative, primaryPropositionId }
  ) {
    if (!primaryPropositionId || !partB || !alternative) return null;

    const propositionIds = [primaryPropositionId, partB.propositionId];
    if (alternative.propositionId !== partB.propositionId) {
      propositionIds.push(alternative.propositionId);
    }

    try {
      const { data } = await lbApiBeta.post(
        '/api/messenger/chat-room/find-or-mock',
        { propositionIds }
      );
      return data || null;
    } catch (e) {
      return null;
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
